import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	DatePicker as ChakraDatePicker,
	HStack,
	Input,
	Portal,
	chakra,
	type DatePickerOpenChangeDetails,
	type DatePickerValueChangeDetails,
	type DateValue,
} from '@chakra-ui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { FiChevronDown, FiClock } from 'react-icons/fi';
import { CalendarDate, parseDate, today } from '@internationalized/date';
import { TIME_INCREMENTS } from '@/utils/guest-access/constants.ts';
import { TRANSITION_BG, TRANSITION_CHEVRON, TRANSITION_DROPDOWN_REVEAL } from '@/components/ui/motion.ts';
import { datePickerPopupStyles } from '@/components/common/access-time-selector/styles.ts';
import { dropdownScrollbarCss } from '@/components/ui/scrollbar.ts';

function toDateString(dv: DateValue): string {
	return `${dv.year}-${String(dv.month).padStart(2, '0')}-${String(dv.day).padStart(2, '0')}`;
}

function toDisplayDateString(dv: DateValue): string {
	return `${String(dv.day).padStart(2, '0')}/${String(dv.month).padStart(2, '0')}/${dv.year}`;
}

function toDateValue(dateStr: string): CalendarDate | undefined {
	if (!dateStr) return undefined;
	try {
		return parseDate(dateStr);
	} catch {
		return undefined;
	}
}

function parseDisplayDate(input: string): CalendarDate | undefined {
	const trimmed = input.trim();
	const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
	if (slashMatch) {
		const [, day, month, year] = slashMatch;
		return toDateValue(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
	}
	return toDateValue(trimmed);
}

interface CustomDateTimePickerProps {
	isOpen: boolean;
	customDate: string;
	customTime: string;
	onDateChange: (date: string) => void;
	onTimeChange: (time: string) => void;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
	isOpen,
	customDate,
	customTime,
	onDateChange,
	onTimeChange,
}) => {
	const timeDropdownRef = useRef<HTMLDivElement>(null);
	const timeTriggerRef = useRef<HTMLButtonElement>(null);
	const [showTimeDropdown, setShowTimeDropdown] = useState(false);
	const [timeDropdownPosition, setTimeDropdownPosition] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);

	const minDate = useMemo(() => today('Pacific/Auckland'), []);
	const dateValue = useMemo(() => {
		const dv = toDateValue(customDate);
		return dv ? [dv] : [];
	}, [customDate]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const clickedTimeTrigger = timeTriggerRef.current?.contains(e.target as Node);
			const clickedTimeDropdown = timeDropdownRef.current?.contains(e.target as Node);
			if (!clickedTimeTrigger && !clickedTimeDropdown) {
				setShowTimeDropdown(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setShowTimeDropdown(false);
			setIsCalendarOpen(false);
		}
	}, [isOpen]);

	const updateTimeDropdownPosition = useCallback(() => {
		const trigger = timeTriggerRef.current;
		if (!trigger) return;

		const rect = trigger.getBoundingClientRect();
		const gap = 4;
		const viewportPadding = 8;
		const dropdownHeight = 180;
		const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
		const shouldOpenUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight + gap;

		setTimeDropdownPosition({
			top: shouldOpenUp ? Math.max(viewportPadding, rect.top - dropdownHeight - gap) : rect.bottom + gap,
			left: rect.left,
			width: rect.width,
		});
	}, []);

	useEffect(() => {
		if (!showTimeDropdown) return;

		updateTimeDropdownPosition();
		window.addEventListener('resize', updateTimeDropdownPosition);
		window.addEventListener('scroll', updateTimeDropdownPosition, true);

		return () => {
			window.removeEventListener('resize', updateTimeDropdownPosition);
			window.removeEventListener('scroll', updateTimeDropdownPosition, true);
		};
	}, [showTimeDropdown, updateTimeDropdownPosition]);

	const handleCalendarOpenChange = (details: DatePickerOpenChangeDetails) => {
		setIsCalendarOpen(details.open);
	};

	const handleDateChange = (details: DatePickerValueChangeDetails) => {
		if (details.value.length > 0) {
			onDateChange(toDateString(details.value[0]));
		}
	};

	const handleDateInputFocus: React.FocusEventHandler<HTMLInputElement> = () => {
		setIsCalendarOpen(true);
	};

	const handleDateInputClick: React.MouseEventHandler<HTMLInputElement> = () => {
		setIsCalendarOpen(true);
	};

	const handleTimeSelect = (time: string) => {
		onTimeChange(time);
		setShowTimeDropdown(false);
	};

	const handleTimeTriggerClick = () => {
		const nextOpen = !showTimeDropdown;
		if (nextOpen) {
			updateTimeDropdownPosition();
		}
		setShowTimeDropdown(nextOpen);
	};

	return (
		<Box pt={4} pb={1} overflow="visible">
			<HStack gap={2}>
				<Box flex="1" minW={0}>
					<ChakraDatePicker.Root
						value={dateValue}
						onValueChange={handleDateChange}
						min={minDate}
						timeZone="Pacific/Auckland"
						colorPalette="brand"
						open={isCalendarOpen}
						onOpenChange={handleCalendarOpenChange}
						closeOnSelect
						format={toDisplayDateString}
						parse={parseDisplayDate}
					>
						<ChakraDatePicker.Control>
							<Box position="relative" w="full">
								<ChakraDatePicker.Trigger asChild>
									<chakra.button
										type="button"
										aria-label="Open calendar"
										position="absolute"
										left={1.5}
										top="50%"
										transform="translateY(-50%)"
										color="fg.muted"
										zIndex={1}
										display="flex"
										alignItems="center"
										justifyContent="center"
										w={8}
										h={8}
										borderRadius="full"
										bg="transparent"
									>
										<CalendarIcon width={16} height={16} />
									</chakra.button>
								</ChakraDatePicker.Trigger>
								<ChakraDatePicker.Input
									asChild
									placeholder="dd/mm/yyyy"
									onFocus={handleDateInputFocus}
									onClick={handleDateInputClick}
								>
									<Input
										rounded="full"
										bg="surface.default"
										textStyle="body.sm"
										fontWeight={500}
										h="auto"
										py={2.5}
										px={4}
										pl={10}
										border="1px solid"
										borderColor={isCalendarOpen ? 'focus.ring' : 'border.default'}
										textAlign="left"
										minW={0}
										fontVariantNumeric="tabular-nums"
										_hover={{ borderColor: isCalendarOpen ? 'focus.ring' : 'border.strong' }}
										_focus={{ outline: 'none', borderColor: 'focus.ring' }}
									/>
								</ChakraDatePicker.Input>
							</Box>
						</ChakraDatePicker.Control>

						<Portal>
							<ChakraDatePicker.Positioner>
								<ChakraDatePicker.Content {...datePickerPopupStyles}>
									<ChakraDatePicker.View view="day">
										<ChakraDatePicker.Header />
										<ChakraDatePicker.DayTable />
									</ChakraDatePicker.View>
									<ChakraDatePicker.View view="month">
										<ChakraDatePicker.Header />
										<ChakraDatePicker.MonthTable />
									</ChakraDatePicker.View>
									<ChakraDatePicker.View view="year">
										<ChakraDatePicker.Header />
										<ChakraDatePicker.YearTable />
									</ChakraDatePicker.View>
								</ChakraDatePicker.Content>
							</ChakraDatePicker.Positioner>
						</Portal>
					</ChakraDatePicker.Root>
				</Box>

				<Box position="relative" flex={{ base: '0 0 104px', sm: '0 0 112px' }}>
					<Button
						ref={timeTriggerRef}
						type="button"
						onClick={handleTimeTriggerClick}
						borderRadius="full"
						border="1px solid"
						borderColor={showTimeDropdown ? 'focus.ring' : 'border.default'}
						bg="surface.default"
						// Chakra's Button defaults to the solid variant, which pairs a solid background with a
						// light foreground. Overriding only `bg` left the time reading white on white — the date
						// trigger beside it escapes this by being a chakra.button rather than a Button.
						color="fg"
						textStyle="body.sm"
						fontWeight={500}
						h="auto"
						py={2.5}
						px={3}
						pl={8}
						pr={6}
						w="full"
						minW={0}
						textAlign="left"
						fontVariantNumeric="tabular-nums"
						_hover={{ borderColor: showTimeDropdown ? 'focus.ring' : 'border.strong' }}
						_focus={{ outline: 'none', borderColor: 'focus.ring' }}
					>
						<Box
							as={FiClock}
							position="absolute"
							left={3}
							top="50%"
							boxSize={4}
							transform="translateY(-50%)"
							color="fg.muted"
						/>
						{customTime}
						<Box
							as={FiChevronDown}
							position="absolute"
							right={3}
							top="50%"
							boxSize={3}
							transform={showTimeDropdown ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)'}
							transition={TRANSITION_CHEVRON}
							color="fg.muted"
						/>
					</Button>
				</Box>
			</HStack>

			<Portal>
				<Box
					ref={timeDropdownRef}
					position="fixed"
					top={timeDropdownPosition?.top ?? 0}
					left={timeDropdownPosition?.left ?? 0}
					w={timeDropdownPosition?.width ?? '112px'}
					minW={{ base: '104px', sm: '112px' }}
					bg="surface.default"
					borderRadius="2xl"
					boxShadow={showTimeDropdown ? 'lg' : 'none'}
					zIndex={1700}
					maxH={showTimeDropdown ? '180px' : '0'}
					opacity={showTimeDropdown && timeDropdownPosition ? 1 : 0}
					overflow="hidden"
					transition={TRANSITION_DROPDOWN_REVEAL}
					pointerEvents={showTimeDropdown ? 'auto' : 'none'}
					visibility={showTimeDropdown ? 'visible' : 'hidden'}
				>
					{/*
					 * The scroll lives here rather than on the box above so its scrollbar is clipped to that
					 * box's radius. Sharing one element ran an 8px bar through a 16px corner, which on a phone
					 * is most of the width the dropdown has.
					 */}
					<Box
						maxH={showTimeDropdown ? '180px' : '0'}
						overflowY={showTimeDropdown ? 'auto' : 'hidden'}
						py={showTimeDropdown ? 1 : 0}
						css={dropdownScrollbarCss}
					>
						{TIME_INCREMENTS.map((timeValue) => (
							<chakra.button
								type="button"
								key={timeValue}
								display="block"
								w="full"
								py={2}
								px={3}
								textStyle="body.sm"
								fontVariantNumeric="tabular-nums"
								fontWeight={customTime === timeValue ? 600 : 400}
								border="none"
								bg={customTime === timeValue ? 'surface.subtle' : 'transparent'}
								cursor="pointer"
								textAlign="left"
								transition={TRANSITION_BG}
								_hover={{ bg: 'surface.subtle' }}
								onClick={() => handleTimeSelect(timeValue)}
							>
								{timeValue}
							</chakra.button>
						))}
					</Box>
				</Box>
			</Portal>
		</Box>
	);
};

export default CustomDateTimePicker;
